import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxBadgeComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxMapRoutePointStatus,
  OnyxToastService,
  OnyxToggleComponent,
  OnyxTooltipComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { map } from 'rxjs';
import type { DictionaryType } from '../../../../../common/enums/dictionary-code';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../common/helpers/route.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../../common/interfaces/validation/validation-group';
import { RouteService } from '../../../../../common/services/route.service';
import { OrderPointCategory } from '../../../common/enums/order-point-category';
import type { OrderLoadingPointFormGroup } from '../../../common/helpers/order-form.helper';
import type { OrderFormPoint } from '../../../common/interfaces/order-form-point';
import { OrderFormService } from '../../../common/services/order-form.service';
import type { OrderCheckpointFormGroup } from '../order-checkpoint-form/order-checkpoint-form.component';
import { OrderCheckpointFormComponent } from '../order-checkpoint-form/order-checkpoint-form.component';
import { OrderLoadingPointFormComponent } from '../order-loading-point-form/order-loading-point-form.component';
import type { OrderTransitFormGroup } from '../order-transit-form/order-transit-form.component';
import { OrderTransitFormComponent } from '../order-transit-form/order-transit-form.component';
import type { OrderUnloadingPointFormGroup } from '../order-unloading-point-form/order-unloading-point-form.component';
import { OrderUnloadingPointFormComponent } from '../order-unloading-point-form/order-unloading-point-form.component';

export type OrderPointFormGroup =
  | OrderLoadingPointFormGroup
  | OrderUnloadingPointFormGroup
  | OrderCheckpointFormGroup
  | OrderTransitFormGroup;
export type OrderPointForm = ReturnType<OrderPointFormGroup['getRawValue']>;

@Component({
  selector: 'app-order-point-form',
  imports: [
    TranslatePipe,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    ReactiveFormsModule,
    OnyxToggleComponent,
    OnyxTooltipComponent,
    OnyxDropdownDirective,
    OrderLoadingPointFormComponent,
    OrderUnloadingPointFormComponent,
    OrderCheckpointFormComponent,
    OrderTransitFormComponent,
    OnyxTooltipDirective,
    OnyxBadgeComponent,
  ],
  templateUrl: './order-point-form.component.html',
  styleUrl: './order-point-form.component.scss',
})
export class OrderPointFormComponent implements OnInit {
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly routeService = inject(RouteService);
  private readonly orderFormService = inject(OrderFormService);

  public readonly point = input.required<OrderFormPoint>();
  public readonly pointTypes = input.required<
    DictionaryType<DictionaryCode.ORDER_POINT_TYPE> | undefined
  >();
  public readonly parametersValidation = input.required<
    ValidationGroup | undefined
  >();

  public readonly movePoint = output<-1 | 1>();
  public readonly removePoint = output();

  protected readonly I18N = 'orders.orderForm.points';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrderPointCategory = OrderPointCategory;

  protected expanded = signal(true);
  protected invalidTimeWindow!: Signal<boolean>;
  protected firstLoadingPoint = this.orderFormService.firstLoadingPoint;
  protected lastUnloadingPoint = this.orderFormService.lastUnloadingPoint;
  protected value = this.orderFormService.value;

  protected pointType = computed(() =>
    this.pointTypes()?.find(({ value }) => value === this.point().type),
  );
  protected isHandlingPoint = computed(() =>
    RouteHelper.isHandlingPoint(this.point()),
  );
  protected isIncludedInOrder = computed(() =>
    RouteHelper.isIncludedInOrder(this.point()),
  );
  protected isCompleted = computed(
    () => this.point().status === OnyxMapRoutePointStatus.COMPLETED,
  );
  protected hasMoveUp = computed(() =>
    this.routeService.hasMove(this.point(), -1),
  );
  protected hasMoveDown = computed(() =>
    this.routeService.hasMove(this.point(), 1),
  );
  protected hasRemove = computed(() =>
    this.routeService.hasRemove(this.point()),
  );
  protected disabled = computed(() => !this.routeService.hasEdit(this.point()));

  protected hasSemiTrailerPickup = computed(
    () =>
      this.point().form.value.uuid ===
      this.orderFormService.value().basicInformation.pickupPointUuid,
  );
  protected hasSemiTrailerDrop = computed(
    () =>
      this.point().form.value.uuid ===
      this.orderFormService.value().basicInformation.dropPointUuid,
  );
  protected dropdownOptions = computed(() => {
    const canSetAsPickupPoint =
      this.point().index <= (this.firstLoadingPoint()?.index ?? -1);
    const canSetAsDropPoint =
      this.lastUnloadingPoint() && this.value().basicInformation.isDropped
        ? this.point().index >= (this.lastUnloadingPoint()?.index ?? -1)
        : false;

    if (
      (!canSetAsPickupPoint && !canSetAsDropPoint) ||
      this.hasSemiTrailerPickup() ||
      this.hasSemiTrailerDrop()
    ) {
      return [];
    }

    return [
      {
        name: `${this.I18N}.setAsPickupPoint`,
        value: () =>
          this.orderFormService.form.patchValue({
            basicInformation: { pickupPointUuid: this.point().value.uuid },
          }),
        disabled: !canSetAsPickupPoint,
      },
      {
        name: `${this.I18N}.setAsDropPoint`,
        value: () =>
          this.orderFormService.form.patchValue({
            basicInformation: { dropPointUuid: this.point().value.uuid },
          }),
        disabled: !canSetAsDropPoint,
      },
    ];
  });

  public ngOnInit(): void {
    this.invalidTimeWindow = toSignal(
      this.point().form.statusChanges.pipe(
        map(() => this.point().form.hasError('invalidTimeWindow')),
      ),
      { initialValue: false, injector: this.injector },
    );

    effect(
      () => {
        const hasPickup = this.hasSemiTrailerPickup();
        const hasDrop = this.hasSemiTrailerDrop();

        if ((hasPickup || hasDrop) && !this.isIncludedInOrder()) {
          this.point().form.patchValue({ includeInOrder: true });
        }

        const controls = this.point().form.controls;
        ValidationHelper.toggleControls(
          'includeInOrder' in controls ? controls.includeInOrder : [],
          {
            enabled: !(hasPickup || hasDrop),
            reset: false,
          },
        );
      },
      { injector: this.injector },
    );
  }

  protected async toggleExpanded(): Promise<void> {
    if (!this.expanded()) {
      this.expanded.set(true);
    } else if (await ValidationHelper.checkValidity(this.point().form, null)) {
      this.expanded.set(false);
    } else {
      this.toastService.showError({ message: `${this.I18N}.collapseError` });
      this.expanded.set(true);
    }
  }
}
